import styled, { css } from "styled-components";
import { Button, Form, Input, Select } from "@renderbus/ui";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { useModalFormStore } from "../../store/modal-form-store";
import GreenWarnIcon from "@/assets/icons/profile/green-warn";
import RedWarnIcon from "@/assets/icons/profile/red-warn";
import { useStudentForm } from "./hooks/useStudentForm";
import { getStatusTranslate } from "./helper/getStatusTranslate";
import { Upload } from "antd";
import { StudentDrawer } from "./atom.js";
import { isEn } from "project-config/utils/env";
import DeleteIcon from "@/assets/icons/profile/delete";
import CameraIcon from "@/assets/icons/profile/camera";
import { useTranslation } from "react-i18next";
import { useVerifyCode } from "./hooks/useVerifyCode";
import { InfoStatus } from "project-config/constants";
import { useGlobalStore } from "@/store/GlobalStore";

export function StudentCertifyDrawer() {
  const t = getTranslateNsT("center");
  const { t: centerT } = useTranslation("center");
  const { openStudentCertifyDrawer, setOpenStudentCertifyDrawer } =
    useModalFormStore();
  const { studentData } = useGlobalStore();
  const {
    form,
    getUploadProps,
    handleSubmit,
    imgUrls,
    uploadFormEvent,
    deleteImgByField,
  } = useStudentForm();
  const { verifyCode, draw } = useVerifyCode("student-verify-code");
  const educationalOptions = [
    centerT("student_mod_identify_start_item_education_levels_0"),
    centerT("student_mod_identify_start_item_education_levels_1"),
    centerT("student_mod_identify_start_item_education_levels_2"),
    centerT("student_mod_identify_start_item_education_levels_3"),
  ].map((label: string, index: number) => ({
    label,
    value: index + 1,
  }));
  const yearOptions = Array.from({ length: 10 }).map((_, index) => {
    const nowYear = new Date().getFullYear();
    return {
      label: nowYear - index,
      value: nowYear - index,
    };
  });
  return (
    <StudentDrawer
      width={900}
      open={openStudentCertifyDrawer}
      className="border-title student-drawer"
      title={t("student_mod_title")}
      onClose={() => {
        setOpenStudentCertifyDrawer(false);
      }}
      drawerContent={
        <ContentWrapper>
          <TipTextArea isRejected={studentData?.status === InfoStatus.Rejected}>
            {studentData?.status === InfoStatus.Rejected ? (
              <RedWarnIcon />
            ) : (
              <GreenWarnIcon />
            )}
            <Tip>
              <span>{t("student_mod_identify_status")}</span>
              <span className="status">
                {getStatusTranslate(
                  studentData ? studentData.status : InfoStatus.NotVerified
                )}
              </span>
              {(!studentData ||
                studentData.status === InfoStatus.NotVerified) && (
                <span>{t("student_mod_identify_status_no_tips")}</span>
              )}
              {studentData?.status === InfoStatus.Rejected && (
                <span>{`（${t("student_mod_identify_resason")}${
                  studentData.remarks
                }）`}</span>
              )}
            </Tip>
          </TipTextArea>
          <Form
            colon={false}
            form={form}
            size={"middle"}
            labelAlign="right"
            requiredMark={false}
            labelCol={{ span: isEn() ? 8 : 5 }}
          >
            <Form.Item
              name="realName"
              label={t("student_mod_identify_start_item_realname")}
              rules={[
                {
                  required: true,
                  message: t("student_mod_identify_start_realname_empty"),
                },
              ]}
            >
              <Input
                placeholder={t(
                  "student_mod_identify_start_item_realname_place"
                )}
                maxLength={20}
              />
            </Form.Item>
            <Form.Item
              name="identitycard"
              label={t("student_mod_identify_start_item_cardno")}
              rules={[
                { required: true, message: t("utils_validate_cardno_empty") },
                {
                  pattern: new RegExp(
                    /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
                    "g"
                  ),
                  message: t("utils_validate_cardno_illegal"),
                },
              ]}
            >
              <Input
                placeholder={t("student_mod_identify_start_item_cardno_place")}
              />
            </Form.Item>
            <Form.Item
              name="schoolAddress"
              label={t("student_mod_identify_start_item_schooladdr")}
              rules={[
                {
                  required: true,
                  message: t("student_mod_identify_start_schooladdr_empty"),
                },
              ]}
            >
              <Input
                placeholder={t(
                  "student_mod_identify_start_item_schooladdr_place"
                )}
                maxLength={20}
              />
            </Form.Item>
            <Form.Item
              name="schoolName"
              label={t("student_mod_identify_start_item_schoolname")}
              rules={[
                {
                  required: true,
                  message: t("student_mod_identify_start_schoolname_empty"),
                },
              ]}
            >
              <Input
                placeholder={t(
                  "student_mod_identify_start_item_schoolname_place"
                )}
                maxLength={20}
              />
            </Form.Item>
            <Form.Item
              name="educational"
              label={t("student_mod_identify_start_item_education")}
              rules={[
                {
                  required: true,
                  message: t("student_mod_identify_qualicard_empty"),
                },
              ]}
            >
              <Select
                options={educationalOptions}
                allowClear={false}
                placeholder={t(
                  "student_mod_identify_start_item_education_place"
                )}
              />
            </Form.Item>
            <Form.Item
              name="timeAgeTemp"
              label={t("student_mod_identify_start_item_timeage")}
              rules={[
                {
                  required: true,
                  message: t("student_mod_identify_timeage_empty"),
                },
              ]}
            >
              <Select
                options={yearOptions}
                allowClear={false}
                placeholder={t(
                  "student_mod_identify_start_item_education_place"
                )}
              />
            </Form.Item>
            <Form.Item
              label={t("student_mod_identify_upload_id_card")}
              style={{ marginBottom: "12px" }}
            >
              <UploadCardWrapper>
                <Form.Item
                  name="behindImage"
                  valuePropName="file"
                  getValueFromEvent={uploadFormEvent}
                  style={{ marginBottom: "0" }}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value && !studentData) {
                          return Promise.reject(
                            t("student_mod_identify_start_item_carbhind_tips")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <UploadWrapper>
                    <Upload {...getUploadProps("behindImage")}>
                      <UploadInner>
                        <ImageWrapper>
                          {!imgUrls.behindImage && (
                            <>
                              <CameraIcon />
                              <div>{t("upload_avatar_side")}</div>
                            </>
                          )}
                          {imgUrls.behindImage && (
                            <>
                              <img
                                src={imgUrls.behindImage}
                                alt="ID card avator side"
                              />
                            </>
                          )}
                        </ImageWrapper>
                      </UploadInner>
                    </Upload>
                    {imgUrls.behindImage && (
                      <DeleteModalBtn
                        onClick={() => {
                          deleteImgByField("behindImage");
                        }}
                      >
                        <DeleteIcon />
                      </DeleteModalBtn>
                    )}
                  </UploadWrapper>
                </Form.Item>
                <Form.Item
                  name="frontImage"
                  valuePropName="file"
                  getValueFromEvent={uploadFormEvent}
                  style={{ marginBottom: "0" }}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value && !studentData) {
                          return Promise.reject(
                            t("student_mod_identify_start_item_cardfront_tips")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <UploadWrapper>
                    <Upload {...getUploadProps("frontImage")}>
                      <UploadInner>
                        <ImageWrapper>
                          {!imgUrls.frontImage && (
                            <>
                              <CameraIcon />
                              <div>{t("upload_flag_side")}</div>
                            </>
                          )}
                          {imgUrls.frontImage && (
                            <img
                              src={imgUrls.frontImage}
                              alt="ID card icon side"
                            />
                          )}
                        </ImageWrapper>
                      </UploadInner>
                    </Upload>
                    {imgUrls.frontImage && (
                      <DeleteModalBtn
                        onClick={() => {
                          deleteImgByField("frontImage");
                        }}
                      >
                        <DeleteIcon />
                      </DeleteModalBtn>
                    )}
                  </UploadWrapper>
                </Form.Item>
              </UploadCardWrapper>
            </Form.Item>
            <Form.Item label=" " style={{ marginBottom: "0px" }}>
              <span
                style={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}
              >
                {t("upload_tip")}
              </span>
            </Form.Item>
            <Form.Item
              name="cardImage"
              label={t("student_mod_identify_start_item_qualicard_tips")}
              valuePropName="file"
              getValueFromEvent={uploadFormEvent}
              style={{ marginTop: "12px", marginBottom: "12px" }}
              rules={[
                {
                  validator: (_, value) => {
                    if (!value && !studentData) {
                      return Promise.reject(
                        t(
                          "student_mod_identify_start_item_qualicard_tips_please"
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <UploadCardWrapper>
                <Upload {...getUploadProps("cardImage")}>
                  <UploadInner>
                    <ImageWrapper>
                      {!imgUrls.cardImage && (
                        <>
                          <CameraIcon />
                          <div>{t("upload_picture")}</div>
                        </>
                      )}
                      {imgUrls.cardImage && (
                        <>
                          <img
                            src={imgUrls.cardImage}
                            alt="Certification file"
                          />
                        </>
                      )}
                    </ImageWrapper>
                  </UploadInner>
                </Upload>
                {imgUrls.cardImage && (
                  <DeleteModalBtn
                    onClick={() => {
                      deleteImgByField("cardImage");
                    }}
                  >
                    <DeleteIcon />
                  </DeleteModalBtn>
                )}
              </UploadCardWrapper>
            </Form.Item>
            <Form.Item label=" " style={{ marginBottom: "24px" }}>
              <span
                style={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}
              >
                {t("upload_tip")}
              </span>
            </Form.Item>
            <Form.Item
              name="verify"
              label={t("student_mod_identify_start_item_verify")}
              initialValue={""}
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.reject(
                        t("student_mod_identify_start_item_verify_place")
                      );
                    }
                    if (value.trim() !== verifyCode) {
                      return Promise.reject(t("validate_code_error"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <VerifyCodeWrapper>
                <Input
                  placeholder={t(
                    "student_mod_identify_start_item_verify_place"
                  )}
                  style={{ width: isEn() ? "200px" : "112px" }}
                />
                <canvas width="90" height="40" id="student-verify-code" />
                <Button type="text" onClick={() => draw()}>
                  {t("fresh")}
                </Button>
              </VerifyCodeWrapper>
            </Form.Item>
            <Form.Item label=" " style={{ marginTop: "56px" }}>
              <Button type="primary" onClick={handleSubmit}>
                {t("student_mod_identify_start_btn_submit")}
              </Button>
            </Form.Item>
          </Form>
        </ContentWrapper>
      }
    />
  );
}

const VerifyCodeWrapper = styled.div`
  display: flex;
  gap: 10px;
  button {
    color: var(--main-color);
    &:focus {
      color: var(--main-color);
    }
  }
`;

const UploadWrapper = styled.div`
  position: relative;
`;

const DeleteModalBtn = styled.div`
  z-index: 1;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 192px;
  height: 120px;
  top: 0;
  left: 0;
  cursor: pointer;
  padding: 9px 7px 8px 9px;
  svg {
    background: rgba(0, 0, 0, 0.6);
    width: 176px;
    height: 104px;
    padding: 40px 76px;
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  gap: 7px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 176px;
  height: 104px;
  background: #333336;
  border-radius: 3px;
  position: absolute;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  img {
    position: absolute;
    width: 100%;
    height: 100%;
  }
`;

const UploadInner = styled.div`
  width: 192px !important;
  height: 120px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 4px;
  position: relative;
  padding: 8px;
  pointer: none;
`;

const UploadCardWrapper = styled.div`
  display: flex;
  gap: 36px;
  position: relative;
`;

const Tip = styled.div`
  margin-left: 10px;
  color: rgba(255, 255, 255, 0.85);
  .status {
    color: var(--main-color);
    margin: 0 5px;
  }
`;

const TipTextArea = styled.div<{ isRejected: boolean }>`
  background: rgba(19, 203, 117, 0.1);
  border: 1px solid rgba(19, 203, 117, 0.26);
  border-radius: 4px;
  min-height: 40px;
  display: flex;
  align-items: center;
  padding: 0 17px;
  margin-bottom: 24px;
  ${(props) =>
    props.isRejected &&
    css`
      background: rgba(255, 68, 68, 0.1);
      border: 1px solid rgba(255, 68, 68, 0.26);
      color: #ff4444;
      ${Tip} {
        color: #ff4444;
      }
      .status {
        color: #ff4444 !important;
      }
    `}
`;

const ContentWrapper = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  .ant-form-item-control-input {
    width: 400px;
  }
  .ant-select {
    width: 220px;
  }
  .ant-form-item-control {
    width: 192px !important;
  }
  .renderbus-ui--select .ant-select-selector {
    background: #333336 !important;
  }
  .ant-form-item-explain {
    position: absolute;
    width: max-content;
    color: rgba(255, 255, 255, 0.85);
    font-size: 12px;
  }
`;
